import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  ClockIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useCallback, useState } from "react";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import {
  formatDocumentChainStepLabel,
  type DocumentChain,
  type DocumentChainStep,
} from "@/lib/dokumente";
import {
  isElectronWithOpenFolder,
  openElectronInOtherWindow,
  otherWindowToastMessage,
} from "@/lib/electron";
import { getOrder } from "@/lib/projectOverview";
import { openOrderFolder } from "@/lib/projects";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { toast } from "@/lib/toast";
import { DOCUMENT_EDITOR_NESTED_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";
import { useOptionalInvoiceDraftContextModals } from "@/pages/dokumente/invoiceDraftContextModals";

const ICON_BTN =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-separator bg-card text-label transition hover:bg-fill-tertiary disabled:opacity-40";

async function openInOtherWindowOrFallback(
  path: string,
  fallback: () => void,
): Promise<void> {
  const target = await openElectronInOtherWindow(path);
  if (target) {
    toast.info(otherWindowToastMessage(target));
    return;
  }
  fallback();
}

export function InvoiceDraftContextButtons({
  auftragId,
  docId,
  chain = null,
  timesheetSteps,
  className,
  buttonClassName,
}: {
  auftragId: number | null | undefined;
  docId: number | null | undefined;
  chain?: DocumentChain | null;
  /** @deprecated Nutze `chain.steps`. */
  timesheetSteps?: DocumentChainStep[];
  className?: string;
  buttonClassName?: string;
}) {
  const { user } = useCurrentUser();
  const contextModals = useOptionalInvoiceDraftContextModals();
  const { open: openProjectOverviewFallback, close: closeProjectOverview } = useProjectOverview();
  const { openDetail: openDetailFallback, close: closeTimesheet } = useTimesheetFlow();
  const [folderBusy, setFolderBusy] = useState(false);

  const id = Number(auftragId);
  const canProject = Number.isFinite(id) && id > 0;
  const documentId = Number(docId);
  const canChain = Number.isFinite(documentId) && documentId > 0;
  const showFolder = userIsBuero(user) && isElectronWithOpenFolder();

  const steps = chain?.steps ?? timesheetSteps ?? [];
  const timesheets = steps.filter(
    (step) =>
      step.kind === "stundennachweis" &&
      step.timesheet_id != null &&
      Number(step.timesheet_id) > 0,
  );

  const handleProjectOverview = useCallback(() => {
    if (!canProject) return;
    if (contextModals) {
      contextModals.openProjectOverview(id);
      return;
    }
    closeTimesheet();
    void openInOtherWindowOrFallback(`/projekte?auftrag_id=${id}`, () => {
      openProjectOverviewFallback(id);
    });
  }, [
    canProject,
    contextModals,
    id,
    closeTimesheet,
    openProjectOverviewFallback,
  ]);

  const openTimesheet = useCallback(
    (timesheetId: number) => {
      if (contextModals) {
        contextModals.openTimesheet(timesheetId);
        return;
      }
      closeProjectOverview();
      void openInOtherWindowOrFallback(
        `/dokumente/stundennachweise?timesheet_id=${timesheetId}`,
        () => openDetailFallback(timesheetId),
      );
    },
    [contextModals, closeProjectOverview, openDetailFallback],
  );

  const handleOpenFolder = useCallback(async () => {
    if (!canProject || folderBusy) return;
    setFolderBusy(true);
    try {
      const order = await getOrder(id);
      await openOrderFolder(order);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Ordner konnte nicht geöffnet werden.");
    } finally {
      setFolderBusy(false);
    }
  }, [canProject, folderBusy, id]);

  const handleOpenChain = useCallback(() => {
    if (!canChain) return;
    if (contextModals) {
      contextModals.openChain();
      return;
    }
    void openInOtherWindowOrFallback(`/dokumente?kette=${documentId}`, () => {
      toast.info("Dokumentenkette ist in diesem Entwurf über die Tabs verfügbar.");
    });
  }, [canChain, contextModals, documentId]);

  if (!canProject && !canChain) return null;

  const btnClass = clsx(ICON_BTN, buttonClassName);

  return (
    <div className={clsx("inline-flex flex-wrap items-center gap-1.5", className)}>
      {canProject ? (
        <ProjectOverviewButton
          auftragId={id}
          className={buttonClassName}
          title="Projektübersicht"
          onOpenOverride={handleProjectOverview}
        />
      ) : null}

      <TimesheetContextButton
        timesheets={timesheets}
        className={btnClass}
        onOpen={openTimesheet}
      />

      {showFolder ? (
        <button
          type="button"
          className={btnClass}
          disabled={!canProject || folderBusy}
          title="Ordner öffnen"
          aria-label="Ordner öffnen"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            void handleOpenFolder();
          }}
        >
          <span className="text-[1.2rem] leading-none" aria-hidden>
            📂
          </span>
        </button>
      ) : null}

      <button
        type="button"
        className={btnClass}
        disabled={!canChain}
        title="Dokumentenkette"
        aria-label="Dokumentenkette"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleOpenChain();
        }}
      >
        <DocumentDuplicateIcon className="h-[1.1rem] w-[1.1rem]" aria-hidden />
      </button>
    </div>
  );
}

function TimesheetContextButton({
  timesheets,
  className,
  onOpen,
}: {
  timesheets: DocumentChainStep[];
  className?: string;
  onOpen: (timesheetId: number) => void;
}) {
  const disabled = timesheets.length === 0;

  if (timesheets.length <= 1) {
    const onlyId = timesheets[0]?.timesheet_id;
    return (
      <button
        type="button"
        className={className}
        disabled={disabled || onlyId == null}
        title="Stundennachweis"
        aria-label="Stundennachweis"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (onlyId != null) onOpen(Number(onlyId));
        }}
      >
        <ClockIcon className="h-[1.1rem] w-[1.1rem]" aria-hidden />
      </button>
    );
  }

  return (
    <Popover className="relative shrink-0">
      {({ close }) => (
        <>
          <PopoverButton
            type="button"
            className={className}
            title="Stundennachweis"
            aria-label="Stundennachweis auswählen"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ClockIcon className="h-[1.1rem] w-[1.1rem]" aria-hidden />
          </PopoverButton>
          <PopoverPanel
            className={clsx(
              "absolute right-0 mt-1 w-56 rounded-ios border border-separator bg-card p-1 shadow-ios-md",
              DOCUMENT_EDITOR_NESTED_OVERLAY_Z,
            )}
          >
            <div className="flex flex-col">
              {timesheets.map((step) => {
                const tid = Number(step.timesheet_id);
                return (
                  <button
                    key={step.key || tid}
                    type="button"
                    className="rounded-md px-3 py-2 text-left text-sm text-label hover:bg-fill-tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(tid);
                      close();
                    }}
                  >
                    {formatDocumentChainStepLabel(step)}
                  </button>
                );
              })}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
